import { adminApi } from '../api/axios';

/* Documents émis par Tribe, archivés dans admin-service :
   GET /facturation/v2/client/documents (tous les documents du client),
   GET /facturation/v2/client/documents/{id}/pdf (PDF archivé, en blob).
   Le portail affiche ces documents et leur PDF ; il n'en fabrique aucun. */

export interface LigneDocument {
  libelle: string;
  quantite: number;
  prixUnitaireHt: number;
  tauxTva: number;
  montantHt: number;
  montantTva: number;
  montantTtc: number;
}

export interface DocumentClient {
  id: number | string;
  type: 'DEVIS' | 'CONTRAT' | 'FACTURE' | 'AVOIR' | string;
  numero: string;
  natureFacture?: 'TOTALITE' | 'ACOMPTE' | 'SOLDE' | null;
  emisLe: string;
  dateEcheance?: string | null;
  questId?: string | null;
  titreMission?: string | null;
  montantHt: number;
  montantTva: number;
  montantTtc: number;
  devise?: string;
  documentOrigineNumero?: string | null;
  statutCertification?: string | null;
  numeroFiscal?: string | null;
  lignes?: LigneDocument[];
  etatPaiement?: string | null;
  resteDuTtc?: number | null;
}

export const TYPES_DOCUMENT: Record<string, string> = {
  DEVIS:   'Devis',
  CONTRAT: 'Contrat',
  FACTURE: 'Facture',
  AVOIR:   'Avoir',
};

export const NATURES: Record<string, string> = {
  TOTALITE: 'Totalité',
  ACOMPTE:  'Acompte',
  SOLDE:    'Solde',
};

export const ETATS_A_REGLER = ['NON_PAYEE', 'PARTIELLEMENT_PAYEE'];

/* Montants en F CFA partout ; les totaux portent aussi « (XOF) », le code
   monétaire qu'utilisent les PDF émis par Tribe. */

/** Montant entier en F CFA, milliers séparés par une espace insécable. */
export function formatMontant(montant?: number | null): string {
  if (montant == null || Number.isNaN(Number(montant))) return '—';
  const n = Math.round(Number(montant));
  const chiffres = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${n < 0 ? '-' : ''}${chiffres} F CFA`;
}

/** Total d'un document : « 354 000 F CFA (XOF) », comme sur le PDF. */
export function formatTotal(montant?: number | null): string {
  const m = formatMontant(montant);
  return m === '—' ? m : `${m}\u00a0(XOF)`;
}

/** Horodatage serveur (ISO) affiché à l'heure de Dakar : « 13 octobre 2026 à 16:56 ». */
export function formatHorodatage(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const jour = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Dakar' });
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Dakar' });
  return `${jour} à ${heure}`;
}

/* « 2026-10-12T10:00 » ou « 2026-10-19 » → « 12/10/2026 », sans conversion de fuseau :
   la date est déjà exprimée à l'heure de Dakar. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

export function messageErreurDocuments(err: any): string {
  const status = err?.response?.status;
  if (status === 403) {
    return 'Votre session ne permet pas de consulter les documents de facturation. Reconnectez-vous, ou écrivez à contact@tribe.sn.';
  }
  if (status === 404) {
    return 'Le service de facturation est momentanément introuvable. Réessayez plus tard, ou écrivez à contact@tribe.sn.';
  }
  if (!err?.response) {
    return 'Impossible de joindre le service de facturation. Vérifiez votre connexion puis rechargez la page.';
  }
  return 'Impossible de charger vos documents de facturation. Réessayez plus tard, ou écrivez à contact@tribe.sn.';
}

export async function chargerDocuments(): Promise<DocumentClient[]> {
  const res = await adminApi.get('/facturation/v2/client/documents');
  return Array.isArray(res.data) ? res.data : [];
}

/** Télécharge le PDF archivé sous « numero.pdf ». Lève l'erreur d'axios en cas d'échec. */
export async function telechargerPdf(doc: DocumentClient): Promise<void> {
  const res = await adminApi.get(`/facturation/v2/client/documents/${doc.id}/pdf`, { responseType: 'blob' });
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
  enregistrerFichier(blob, `${doc.numero || `document-${doc.id}`}.pdf`);
}

export function messageErreurPdf(doc: DocumentClient, err: any): string {
  return err?.response?.status === 404
    ? `Le PDF du document ${doc.numero} est introuvable. Écrivez à contact@tribe.sn.`
    : `Impossible de télécharger le PDF du document ${doc.numero}. Veuillez réessayer.`;
}

/** Propose un fichier au téléchargement depuis un blob. */
export function enregistrerFichier(blob: Blob, nom: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Documents d'une mission, du plus récent au plus ancien. */
export function documentsDeLaMission(documents: DocumentClient[], questId: string): DocumentClient[] {
  return documents
    .filter(d => d.questId === questId)
    .sort((a, b) => (b.emisLe || '').localeCompare(a.emisLe || ''));
}
